import { InfiniteData, QueryClient } from '@tanstack/react-query';

import MedicineDictionaryModel from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import { useGetKeywordSearchMagazineCursorPageQuery } from '@/hooks/magazine/mobile/magazine.gql.impl';
import { GetSearchMedicineDictionaryByCursorScorePageQuery } from '@/types/bff/gql-schema';
import { MagazineType } from '@/types/magazine';
import useGetKeywordSearchMedicineDictionaryInfiniteQuery from './useGetKeywordSearchMedicineDictionaryInfiniteQuery';

const parseMedicineDictionary = (
  data: Array<GetSearchMedicineDictionaryByCursorScorePageQuery>
): Array<MedicineDictionaryModel> => {
  if (data) {
    return data
      .flatMap((page) => page.searchMedicineDictionaryByCursorScorePage.items)
      .map((medicine) => {
        return new MedicineDictionaryModel({
          id: medicine.id,
          publicId: medicine.publicId,
          basicInfo: {
            ...medicine.basicInfo,
            medicineImage: medicine.basicInfo.medicineImage ?? undefined,
          },
          medicineGuide: {
            wholeSummary: medicine.medicineGuide?.wholeSummary.join('') ?? '',
          },
          packagingInfo: {
            image: medicine.packagingInfo.image ?? undefined,
          },
        });
      });
  }
  return [] as Array<MedicineDictionaryModel>;
};

interface GetKeywordSearchMedicineDictionaryInfiniteQuery {
  medicineDictionariesData: InfiniteData<MedicineDictionaryModel> | undefined;
  isMedicineDictionariesLoading: boolean;
  hasMedicineDictionariesNextPage?: boolean;
  fetchMedicineDictionariesNextPage: () => void;
}

interface Props {
  keyword: string;
  size?: number;
  contentType?: MagazineType;
  enabled?: boolean;
}

const MEDICINE_DICTIONARIES_SIZE = 20;

const useGetKeywordSearchMedicineDictionaryInfinite = ({
  keyword,
  size = MEDICINE_DICTIONARIES_SIZE,
  enabled = true,
}: Props): GetKeywordSearchMedicineDictionaryInfiniteQuery => {
  const {
    data: medicineDictionariesData,
    isLoading: isMedicineDictionariesLoading,
    hasNextPage: hasMedicineDictionariesNextPage,
    fetchNextPage: fetchMedicineDictionariesNextPage,
  } = useGetKeywordSearchMedicineDictionaryInfiniteQuery(
    {
      request: {
        keyword,
        limit: size,
      },
    },
    {
      select: (data) => {
        return {
          pages: parseMedicineDictionary(data.pages),
          pageParams: data.pageParams,
        };
      },
      enabled: enabled && !!keyword && keyword.length > 0,
    }
  );

  return {
    medicineDictionariesData,
    isMedicineDictionariesLoading,
    hasMedicineDictionariesNextPage,
    fetchMedicineDictionariesNextPage,
  };
};

export default useGetKeywordSearchMedicineDictionaryInfinite;

useGetKeywordSearchMedicineDictionaryInfinite.prefetchQuery = async (
  queryClient: QueryClient,
  params: Omit<Props, 'enabled'>
) => {
  await queryClient.prefetchInfiniteQuery(
    useGetKeywordSearchMagazineCursorPageQuery.getKey({
      request: params,
    }),
    useGetKeywordSearchMagazineCursorPageQuery.fetcher({
      request: params,
    })
  );
};
